import { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';

import Select from '~/common/components/Selects/Select';
import MultiSelect from '~/common/mui/Inputs/Selects/MultiSelect/MultiSelect';

import { useQuestionLibraryStore } from '../../store/questionLibraryStore';

type QuestionLibrarySelectOption = {
  label?: string;
  value?: string;
  isDisabled?: boolean;
  isCustom?: boolean;
};

type QuestionLibraryDynamicOption = {
  dataSource: 'screenData' | 'formData';
  key: string;
};

// Helper function to convert string to kebab-case
const toKebabCase = (str: string): string => {
  return str
    .trim()
    .toLowerCase()
    .replaceAll(/\s+/g, '-')
    .replaceAll(/[^\w-]/g, '');
};

type QuestionLibrarySelectProps = {
  field?: any;
  question?: any;
  isInvalid?: boolean;
  disabled?: boolean;
  maxInputWidth?: string;
  options?: QuestionLibrarySelectOption[] | QuestionLibraryDynamicOption;
};

export default function QuestionLibrarySelect({
  field,
  question,
  options,
  isInvalid,
  maxInputWidth = '100%',
  disabled,
}: Readonly<QuestionLibrarySelectProps>) {
  const { name, metadata, isCreatable } = question;

  const screenDataOptions = useQuestionLibraryStore(
    useShallow((s) => {
      if ('dataSource' in options) {
        if (options.dataSource === 'screenData') {
          const dataKey = options.key.replaceAll('{i}', metadata?.index ?? '0');
          return s.screenData[dataKey];
        }
      }
      return undefined;
    }),
  );
  const dynamicOptions = useMemo(() => {
    if (Array.isArray(options)) {
      return options;
    }
    if (screenDataOptions) {
      return screenDataOptions;
    }
    return [];
  }, [options, screenDataOptions]);

  // Use MultiSelect with freeSolo for creatable dropdowns
  if (isCreatable) {
    const currentValue = field.value
      ? dynamicOptions.find((opt) => opt.value === field.value) || {
          label: field.value,
          value: field.value,
        }
      : null;

    return (
      <MultiSelect
        label={question.label || ''}
        isLabelHidden={true}
        value={currentValue}
        options={dynamicOptions}
        onChange={(_event, selectedOption) => {
          if (typeof selectedOption === 'string') {
            // User typed a custom value - convert to kebab-case for value
            const kebabValue = toKebabCase(selectedOption);
            field.onChange(kebabValue);
          } else if (selectedOption && typeof selectedOption === 'object') {
            // User selected an option
            field.onChange(selectedOption.value);
          } else {
            // Cleared selection
            field.onChange('');
          }
        }}
        freeSolo={true}
        autoSelect={true}
        selectOnFocus={true}
        clearOnBlur={true}
        handleHomeEndKeys={true}
        getOptionLabel={(option) => {
          if (typeof option === 'string') return option;
          return option.label || option.value;
        }}
        isOptionEqualToValue={(option, value) => {
          if (typeof value === 'string') {
            return option.value === value || option.label === value;
          }
          return option.value === value?.value;
        }}
        getOptionDisabled={(option) => option.isDisabled || false}
        disabled={disabled}
        width={maxInputWidth}
        errors={isInvalid}
        id={name}
        data-testid={name}
        aria-labelledby={`${name}-label`}
        placeholder="Select or type an option"
      />
    );
  }

  // Use standard HTML select for non-creatable dropdowns
  return (
    <Select
      placeholder="Select an option"
      name={name}
      width={maxInputWidth}
      isDisabled={disabled}
      isInvalid={isInvalid}
      data-testid={name}
      {...field}
      {...question}
      aria-labelledby={`${name}-label`}
    >
      {dynamicOptions.map((option) => (
        <option
          key={option.value}
          value={option.value}
          disabled={option.isDisabled}
        >
          {option.label}
        </option>
      ))}
    </Select>
  );
}
